import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faFilePdf,
  faFileWord,
  faFileLines,
  faRotateRight
} from '@fortawesome/free-solid-svg-icons';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { saveAs } from 'file-saver';
import { useNavigate, useParams } from 'react-router-dom';
import LeftBar from '../../Components/LeftBar';
import Dashnav from '../../Components/Dashnav';

const DOCUMENT_GENERATION_API_BASE = (
  process.env.REACT_APP_DOCUMENT_GENERATION_API_BASE
  || 'https://us-central1-ubunifu-college.cloudfunctions.net/documentGenerationApi'
);

const DOCUMENT_GENERATION_SHARED_SECRET = process.env.REACT_APP_DOCUMENT_GENERATION_SHARED_SECRET || '';

const DOCUMENT_TYPE_LABELS = {
  recommendation: 'Recommendation Letter',
  confirmation: 'Student Confirmation Letter',
  attachment: 'Attachment / Internship Letter',
  boardResolution: 'Board Resolution Letter',
  minutes: 'Minutes Letter',
  custom: 'Custom Business Letter'
};

const normalizeText = (value) => String(value || '').trim();

const formatDocumentDate = (value, fallback = 'Not dated') => {
  if (!value) return fallback;

  if (typeof value === 'string') {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleString('en-KE', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    }
  }

  if (value?.toDate) {
    return value.toDate().toLocaleString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  const timestampSeconds = Number(value?.seconds ?? value?._seconds);
  const timestampMilliseconds = Number(value?.milliseconds ?? value?._milliseconds);

  if (Number.isFinite(timestampSeconds)) {
    return new Date(timestampSeconds * 1000).toLocaleString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  if (Number.isFinite(timestampMilliseconds)) {
    return new Date(timestampMilliseconds).toLocaleString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  return fallback;
};

function GeneratedDocumentView() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const previewFrameRef = useRef(null);
  const [documentRecord, setDocumentRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState('');
  const [error, setError] = useState('');

  const loadDocument = async () => {
    if (!documentId) {
      setError('Missing document id');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${DOCUMENT_GENERATION_API_BASE}/documents/${documentId}`, {
        headers: {
          ...(DOCUMENT_GENERATION_SHARED_SECRET
            ? { 'x-document-generation-secret': DOCUMENT_GENERATION_SHARED_SECRET }
            : {})
        }
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Unable to load document');
      }

      setDocumentRecord(data.document || null);
    } catch (requestError) {
      setError(requestError?.message || 'Failed to load document');
      setDocumentRecord(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocument();
  }, [documentId]);

  const documentTypeLabel = useMemo(() => (
    DOCUMENT_TYPE_LABELS[documentRecord?.documentType]
    || documentRecord?.documentLabel
    || documentRecord?.documentType
    || 'Document'
  ), [documentRecord]);

  const fileBaseName = useMemo(() => {
    const reference = normalizeText(documentRecord?.referenceNumber);
    const subject = normalizeText(documentRecord?.subject || documentRecord?.document?.subject || documentRecord?.documentLabel);
    const student = normalizeText(documentRecord?.studentName || documentRecord?.document?.studentName || documentRecord?.businessSubject || documentRecord?.meetingTitle);

    return [reference, subject, student]
      .filter(Boolean)
      .join('_')
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9_-]/g, '') || 'document';
  }, [documentRecord]);

  const getPreviewDocument = () => (
    previewFrameRef.current?.contentDocument
    || previewFrameRef.current?.contentWindow?.document
    || null
  );

  const waitForPreviewAssets = async () => {
    const frameDocument = getPreviewDocument();
    if (!frameDocument) {
      return null;
    }

    if (frameDocument.fonts?.ready) {
      try {
        await frameDocument.fonts.ready;
      } catch (fontError) {
        console.warn('Could not wait for preview fonts:', fontError);
      }
    }

    const images = Array.from(frameDocument.images || []);
    await Promise.all(images.map((image) => (
      image.complete
        ? Promise.resolve()
        : new Promise((resolve) => {
            image.onload = resolve;
            image.onerror = resolve;
          })
    )));

    return frameDocument;
  };

  const downloadWordDocument = () => {
    if (!documentRecord?.html) {
      return;
    }

    const blob = new Blob([documentRecord.html], { type: 'application/msword;charset=utf-8' });
    saveAs(blob, `${fileBaseName}.doc`);
  };

  const downloadPdfDocument = async () => {
    if (!documentRecord?.html) {
      return;
    }

    setExporting('pdf');

    try {
      const contentDocument = await waitForPreviewAssets();
      const target = contentDocument?.body || contentDocument?.documentElement;

      if (!target) {
        return;
      }

      const canvas = await html2canvas(target, {
        scale: 3,
        useCORS: true,
        backgroundColor: '#ffffff',
        scrollX: 0,
        scrollY: 0,
        windowWidth: target.scrollWidth,
        windowHeight: target.scrollHeight
      });

      const pdf = new jsPDF({
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
        compress: true
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imageWidth = pageWidth;
      const pageCanvasHeight = Math.max(1, Math.floor((canvas.width * pageHeight) / pageWidth));
      const totalPages = Math.max(1, Math.ceil(canvas.height / pageCanvasHeight));

      for (let pageIndex = 0; pageIndex < totalPages; pageIndex += 1) {
        const sourceY = pageIndex * pageCanvasHeight;
        const sliceHeight = Math.min(pageCanvasHeight, canvas.height - sourceY);
        const pageCanvas = window.document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = sliceHeight;

        const context = pageCanvas.getContext('2d');
        if (!context) {
          continue;
        }

        context.drawImage(
          canvas,
          0,
          sourceY,
          canvas.width,
          sliceHeight,
          0,
          0,
          canvas.width,
          sliceHeight
        );

        const pageImage = pageCanvas.toDataURL('image/png', 1);
        const renderedHeight = (sliceHeight * imageWidth) / canvas.width;

        if (pageIndex > 0) {
          pdf.addPage();
        }

        pdf.addImage(pageImage, 'PNG', 0, 0, imageWidth, renderedHeight, undefined, 'FAST');
      }

      pdf.save(`${fileBaseName}.pdf`);
    } finally {
      setExporting('');
    }
  };

  return (
    <div className="mainContainer">
      <LeftBar />
      <div className="theMainContainer">
        <Dashnav />
        <div className="clientsComp applicationsPage businessInfoPage">
          <div className="appsHeader">
            <div>
              <p className="appsKicker">Business Info</p>
              <h4>Document View</h4>
              <p className="appsSubtext">
                Review the saved document in its rendered format, then download a Word or PDF copy from here.
              </p>
            </div>
            <div className="appsHeaderStats">
              <div className="appsStatChip">
                <span>Type</span>
                <strong>{documentTypeLabel}</strong>
              </div>
              <div className="appsStatChip">
                <span>Reference</span>
                <strong>{documentRecord?.referenceNumber || '—'}</strong>
              </div>
              <div className="appsStatChip">
                <span>Saved</span>
                <strong>{formatDocumentDate(documentRecord?.createdAt || documentRecord?.updatedAt)}</strong>
              </div>
            </div>
          </div>

          <div className="appsFiltersCard">
            <Button variant="outline-dark" onClick={() => navigate('/generated-documents')}>
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Back to archive
            </Button>
            <Button variant="outline-dark" onClick={downloadPdfDocument} disabled={!documentRecord?.html || exporting === 'pdf'}>
              <FontAwesomeIcon icon={faFilePdf} className="me-2" />
              {exporting === 'pdf' ? 'Preparing PDF...' : 'Download PDF'}
            </Button>
            <Button variant="outline-dark" onClick={downloadWordDocument} disabled={!documentRecord?.html}>
              <FontAwesomeIcon icon={faFileWord} className="me-2" />
              Download Word
            </Button>
            <Button variant="outline-dark" onClick={loadDocument} disabled={loading}>
              <FontAwesomeIcon icon={faRotateRight} className="me-2" />
              Reload
            </Button>
          </div>

          {loading ? (
            <div className="appsTableCard">
              <div className="incomeStateCard">
                <Spinner animation="border" role="status" />
                <p>Loading document...</p>
              </div>
            </div>
          ) : error ? (
            <div className="appsTableCard">
              <div className="incomeStateCard incomeEmptyState">
                <h6>Could not open the document</h6>
                <p>{error}</p>
              </div>
            </div>
          ) : (
            <>
              <div className="appsInsightsCard">
                <div className="d-flex flex-wrap justify-content-between gap-3 align-items-start">
                  <div>
                    <p className="appsKicker">Saved record</p>
                    <h6 className="mb-1">
                      <FontAwesomeIcon icon={faFileLines} className="me-2" />
                      {documentRecord?.subject || documentRecord?.document?.subject || documentRecord?.documentLabel || 'Untitled document'}
                    </h6>
                    <p className="appsSubtext mb-0">
                      {documentRecord?.studentName
                        ? `${documentRecord.studentName}${documentRecord?.admissionNumber ? ` • ${documentRecord.admissionNumber}` : ''}`
                        : documentRecord?.businessSubject || documentRecord?.meetingTitle || 'No student metadata'}
                    </p>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <span className="cohortStatusBadge cohortStatusBadge--joined">
                      Saved on {formatDocumentDate(documentRecord?.createdAt || documentRecord?.updatedAt)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="appsTableCard">
                <div className="appsTableHeader">
                  <h6>Preview</h6>
                  <span>{documentRecord?.referenceNumber || 'No reference'}</span>
                </div>
                <div style={{ minHeight: '74vh' }}>
                  <iframe
                    ref={previewFrameRef}
                    title="Generated document preview"
                    srcDoc={documentRecord?.html || ''}
                    style={{
                      width: '100%',
                      height: '74vh',
                      border: '0',
                      background: 'transparent',
                      display: 'block'
                    }}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default GeneratedDocumentView;
